/** @format */

import { Card, Col, Menu, Row, Spin } from "antd";
import axios from "axios";
import Link from "next/link";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { convert } from "html-to-text";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import type { Article, Articles } from "../../../interfaces/articles";
import responsiveColumn from "../../../methods/responsiveColumn";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import Footer from "../../components/Footer";
import { getStaticProps } from "../../../methods/i18n";
import type { GetStaticPaths } from "next";
import SEO from "../../components/seo";
import { useTranslation } from "next-i18next";
import { HeadScriptTag } from "../../components/HeadScriptTag";
import { replaceSpacesAndHyphensWithUnderscores } from "../../components/utils";

export { getStaticProps };

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
    return {
        paths: [],
        fallback: "blocking",
    };
};

export default function PracticeArea() {
    const [areas, setAreas] = useState<Articles>();
    const [selectedArea, setSelectedArea] = useState<Article>();
    const router = useRouter();
    const { slug } = router.query;
    const { locale } = router;
    const { t } = useTranslation(["breadcrumb_section", "controversy", "meta_tags"]);
    const setQuery = useMemo(() => {
        if (locale !== "en") {
            return `?lang=${locale}&`;
        } else {
            return "?";
        }
    }, [locale]);

    const fetchAreas = async () => {
        try {
            const r = await axios({
                url: `https://wp.taxworkoutgroup.com/wp-json/wp/v2/practice_areas${setQuery}_embed&categories=17&per_page=100`,
                method: "get",
            });
            setAreas(
                r.data?.sort((a: any, b: any) => a.acf.priority - b.acf.priority)
            );
            setSelectedArea(
                (r.data as Articles)?.find((article) => article.slug === slug)
            );
        } catch (e) {
            console.log(e);
        }
    };
    useEffect(() => {
        fetchAreas();
    }, [locale]);

    useEffect(() => {
        if (!areas) return;
        setSelectedArea(areas.find((article) => article?.slug === slug));
    }, [slug, areas]);

    useEffect(() => {
        //FIND ALL LIs
        const lis = document.querySelectorAll("li");
        lis?.forEach((li) => {
            if (li.style.listStyleType === "none") li.classList.add("no-before");
        });
    }, [selectedArea]);

    const [modifiedLocationTitle, setModifiedLocationTitle] = useState<string>("");
    const [cleanedBreadcrumbLabel, setCleanedBreadcrumbLabel] = useState<string>("");

    useEffect(() => {
        if (slug) {
            const modified = replaceSpacesAndHyphensWithUnderscores(slug as string);
            setModifiedLocationTitle(modified);
        }
    }, [slug]);

    const [locationtypeData, setLocationTypeData] = useState<any>();
    const [locationData, setLocationData] = useState<any>();
    const [isMapMounted, setIsMapMounted] = useState<any>(true);

    useEffect(() => {
        if (locationData?.length > 0) {
            const filteredLocationsData = locationData?.find(
                (locationValue: any) => locationValue?.slug === slug
            );
            setLocationTypeData(filteredLocationsData);
            setIsMapMounted(false);
        }
    }, [slug, locationData]);

    useEffect(() => {
        let timer: any;
        if (!isMapMounted) {
            timer = setTimeout(() => setIsMapMounted(true), 500);
        }
        return () => clearTimeout(timer);
    }, [isMapMounted]);

    const modifiedLocation: string = replaceSpacesAndHyphensWithUnderscores(
        slug as string
    );

    // const breadcrumbLabel = selectedArea
    // 	? t(`${modifiedLocation}`, { ns: "meta_tags" })
    // 	: "Tax Motivated Bankruptcy Filings";

    useEffect(() => {
        const title = selectedArea ? t(`${modifiedLocation}`, { ns: "meta_tags" }) : t("home");
        const description = selectedArea
            ? t(`${modifiedLocation}_desc`, { ns: "meta_tags" })
            : t("home_desc");
    }, [modifiedLocation, selectedArea]);


    const title = selectedArea ? `${selectedArea?.title.rendered} | Tax Controversy | Practice Areas | Tax Workout Group` : t("meta_tags:home");
    const description = t("meta_tags:home_desc");
    const keywords = "Tax,Tax Bankruptcy ,Tax Controversy";
    // const ogImage = "https://www.example.com/static/images/og-image.jpg";
    const ogType = "website";
    const canonicalURL = selectedArea ? `https://taxworkoutgroup.com/criminal-tax-defense/${(selectedArea?.slug as string)}` : "https://www.taxworkoutgroup.net";


    return (
        <>
            <SEO
                title={selectedArea ? t(`${modifiedLocation}`, { ns: "meta_tags" }) : t(title)}
                description={selectedArea ? t(`${modifiedLocation}_desc`, { ns: "meta_tags" }) : t(description)}
                keywords={keywords}
                ogType={ogType}
                canonicalURL={canonicalURL}
            />
            <HeadScriptTag />
            <CustomBreadcrumb
                items={[
                    {
                        label: t("breadcrumb_section:home"),
                        href: "/",
                    },
                    {
                        label: t("controversy:practice"),
                        href: "/practice-areas",
                    },
                    {
                        label: t("controversy:controversy"),
                        href: "/taxcontroversy",
                    },
                    {
                        label:
                            `${selectedArea?.title.rendered ?? ""} ` ??
                            "Tax Motivated Bankruptcy Filings",
                        href: `/taxcontroversy/${encodeURI(
                            selectedArea?.slug as unknown as string
                        )}`,
                    },
                ]}
            />
            <Spin spinning={selectedArea ? false : true}>
                <Row
                    justify={"center"}
                    style={{ paddingTop: 30, paddingBottom: 30, lineHeight: 1.5 }}
                    gutter={[30, 30]}>
                    <Col
                        {...responsiveColumn([10, 13, 16, 23, 23, 23])}
                        className='practice_article'>
                        <div className='section_title'>
                            <h1 className='section_title'>
                                {selectedArea && convert(selectedArea?.title.rendered)}
                            </h1>
                        </div>
                        <div
                            className='wp taxcontroversy-font-size'
                            style={{ marginTop: 30 }}
                            dangerouslySetInnerHTML={{
                                __html: selectedArea?.content.rendered ?? "",
                            }}
                        />
                    </Col>
                    <Col {...responsiveColumn([6, 6, 7, 23, 23, 23])}>
                        <Card
                            headStyle={{
                                whiteSpace: "normal",
                                textOverflow: "unset",
                                padding: "24px 16px",
                            }}
                            title={
                                <div
                                    style={{
                                        textAlign: "center",
                                        fontSize: 24,
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                    }}>
                                    Tax Controversy <br />
                                    Practice Areas
                                </div>
                            }
                            style={{ boxShadow: "0px 0px 12px rgba(0, 0, 0, 0.25)" }}>
                            <Menu
                                defaultSelectedKeys={[
                                    encodeURI(selectedArea?.title.rendered ?? "0"),
                                ]}
                                mode='vertical'
                                disabledOverflow={true}
                                style={{ border: 0 }}
                                items={areas?.map((area) => {
                                    return {
                                        label: (
                                            <Link
                                                style={{ fontSize: 16 }}
                                                href={`/taxcontroversy/${area.slug}`}>
                                                {area.title.rendered}
                                            </Link>
                                        ),
                                        key: area.id,
                                        style: {
                                            whiteSpace: "normal",
                                            height: "auto",
                                            lineHeight: 1.6,
                                            padding: "12px 24px",
                                        },
                                    };
                                })}
                            />
                        </Card>
                    </Col>
                </Row>
            </Spin>
            <Footer />
        </>
    );
}
